/* The split test log behind every pin. Sample data in the operator voice,
   written to be edited. Order here is the pin order on the page.
   Rule: no em dashes. */

export const LANDING_PINS = [
  {
    id: "hero",
    block: "Hero, above the fold",
    piece: "Headline and primary call to action",
    state: "validated",
    hypothesis:
      "Cold traffic does not know what Hashi-aid is, so naming the outcome beats naming the product. Sending them into a quiz instead of a cart also removes the choice they cannot make yet.",
    metric: "Quiz start rate from paid traffic, then cost per quiz completion",
    control: ["Hashi-aid, daily thyroid support", "Add to cart, $39"],
    variant: ["Your supplement routine, simplified", "Start the quiz, 60 seconds"],
    result: "Quiz starts 41.2% to 52.8% across 18 days, 6,400 sessions a side. Cost per completion fell 24%.",
    shipped:
      "Variant is now the control on every paid landing session. Add to cart stayed on branded search where people already know the SKU.",
  },
  {
    id: "trustbar",
    block: "Trust strip under the hero",
    piece: "Which three proof points sit closest to the button",
    state: "repeatable",
    hypothesis:
      "Reviewer volume does more work than shipping perks this high on the page, because the objection at this point is whether the brand is real.",
    metric: "Scroll past 50% and quiz start rate",
    control: ["Free shipping over $50", "30 day returns", "Made in the UK"],
    variant: ["1,571 verified reviews", "Third party tested every batch", "Formulated with a thyroid dietitian"],
    result: "Quiz starts up 8.4%. Held on a second cohort in month two, so it is not a novelty read.",
    shipped: "Variant shipped. Shipping and returns moved down to the guarantee bar near the footer.",
  },
  {
    id: "steps",
    block: "Three step strip",
    piece: "Whether the funnel is explained before it is entered",
    state: "validated",
    hypothesis:
      "People abandon quizzes because they do not know what they get at the end. Showing quiz, match, offer up front sets the payoff before the first question.",
    metric: "Quiz completion rate, step one to result",
    control: ["No strip. Hero straight into the mechanism section."],
    variant: ["Quiz, then Match, then Offer, with an icon each and the time it takes"],
    result: "Completion 63% to 78%. Biggest single lift on the page so far.",
    shipped: "Variant shipped and the same strip was reused inside the quiz header.",
  },
  {
    id: "mechanism",
    block: "Gut and thyroid mechanism",
    piece: "Diagram against plain benefit copy",
    state: "directional",
    hypothesis:
      "This buyer has already read a lot. A mechanism they can point at should beat generic support language, because it explains why this is not the same as the bottle they already tried.",
    metric: "Time on section and add to cart rate after the quiz result",
    control: ["Supports thyroid health", "Supports energy", "Supports immunity"],
    variant: ["Labelled gut and thyroid axis diagram", "Selenium, zinc, vitamin D, methylated B12 called out on the image"],
    result: "Add to cart up 5.1% but only 780 orders a side. Not called yet, running another 14 days.",
    shipped: "Nothing shipped. Variant is still holding 50% of traffic until the sample is big enough.",
  },
  {
    id: "compare",
    block: "Them versus Axos table",
    piece: "Whether a competitor comparison earns the space",
    state: "repeatable",
    hypothesis:
      "Most of this audience already owns a generic thyroid supplement. Naming what that bottle does not do reframes the price instead of defending it.",
    metric: "Add to cart rate and refund rate at 60 days",
    control: ["Ingredient table only"],
    variant: ["Five line side by side against a generic thyroid supplement, then the quiz call to action"],
    result: "Add to cart up 11.7%, refunds flat. Repeated on the advertorial traffic with the same direction.",
    shipped: "Variant shipped on the landing page and the listicle. Kept off email to avoid sounding defensive to buyers.",
  },
  {
    id: "reviews",
    block: "Reviews",
    piece: "Star summary against verbatim quotes",
    state: "validated",
    hypothesis:
      "The specific sentence a customer wrote does more than an average score, because the score reads like every other brand's score.",
    metric: "Add to cart rate from the reviews section",
    control: ["4.7 average, 1,571 reviews, bar chart"],
    variant: ["Three verbatim quotes with the condition named, score kept small above them"],
    result: "Add to cart up 9.3% over 21 days. Quotes also became the top performing ad hooks.",
    shipped: "Variant shipped. The winning quote is now pulled straight into the Meta batch every Monday.",
  },
  {
    id: "faq",
    block: "Frequently asked questions",
    piece: "Which objection sits at position one",
    state: "fatigued",
    hypothesis:
      "Leading with the interaction question, can I take this with levothyroxine, should clear the biggest blocker earlier than the shipping question did.",
    metric: "Add to cart rate and support tickets per hundred orders",
    control: ["When will my order arrive?"],
    variant: ["Can I take this alongside levothyroxine?"],
    result: "Won by 6.2% for seven weeks, then flattened once the same line was added to the ads and the quiz result.",
    shipped:
      "Variant kept, but the test is retired. The answer now appears earlier in the funnel, so this slot no longer moves anything.",
  },
  {
    id: "stickycta",
    block: "Sticky bottom bar",
    piece: "Whether a persistent bar helps or annoys",
    state: "candidate",
    hypothesis:
      "On mobile the quiz button leaves the screen for most of the page. A slim persistent bar should recover the people who decided halfway down.",
    metric: "Mobile quiz start rate, bounce rate as a guardrail",
    control: ["No bar. Quiz call to action only in the hero and the footer."],
    variant: ["Slim bar, product name plus Start the quiz, appears after 40% scroll"],
    result: "Not live yet. Built and queued behind the mechanism test so the two do not overlap.",
    shipped: "Nothing yet. Goes live Monday at 50% of mobile traffic.",
  },
];

export const QUIZ_PINS = [
  {
    id: "quiz-intro",
    block: "Quiz opening screen",
    piece: "Question count promise",
    state: "validated",
    hypothesis:
      "Telling people exactly how many questions there are removes the fear of an endless form, so more of them start.",
    metric: "Start rate from the landing page",
    control: ["Find your match"],
    variant: ["Three questions. About sixty seconds."],
    result: "Start rate 52.8% to 61.4% over two weeks.",
    shipped: "Variant shipped and the same promise now appears in the ad copy.",
  },
  {
    id: "quiz-q1",
    block: "Question one",
    piece: "What gets asked first",
    state: "repeatable",
    hypothesis:
      "Opening with the diagnosis is too heavy for a first click. Asking what they want to improve is easier and still routes correctly.",
    metric: "Step one to step two drop off",
    control: ["Have you been diagnosed with a thyroid condition?"],
    variant: ["What are you trying to improve first?"],
    result: "Drop off at step one 22% to 11%. Repeated across two audiences.",
    shipped: "Variant shipped. The diagnosis question moved to step two where intent is already committed.",
  },
  {
    id: "quiz-progress",
    block: "Progress indicator",
    piece: "Bar against a step counter",
    state: "directional",
    hypothesis: "A filling bar reads as shorter than the words step two of three.",
    metric: "Completion rate",
    control: ["Step 2 of 3"],
    variant: ["Filling bar with the remaining time under it"],
    result: "Completion up 3.1%. Small, still running.",
    shipped: "Nothing shipped yet.",
  },
  {
    id: "quiz-email",
    block: "Email capture before the result",
    piece: "Whether the email gate sits before or after the match",
    state: "validated",
    hypothesis:
      "Gating the result collects more addresses but costs orders. Showing the match first and offering to save it should trade a few addresses for more revenue.",
    metric: "Email capture rate and revenue per quiz completion",
    control: ["Enter your email to see your match"],
    variant: ["Your match is Hashi-aid. Want it saved and your offer sent?"],
    result: "Capture 71% to 58%, revenue per completion up 19%. Net positive, so it ships.",
    shipped: "Variant shipped. The lost addresses are recovered by the abandonment flow instead.",
  },
  {
    id: "quiz-result",
    block: "Result screen",
    piece: "How the offer is framed on the match",
    state: "candidate",
    hypothesis:
      "Framing the discount as the reason to subscribe rather than a coupon should lift subscription share without touching one time conversion.",
    metric: "Subscription share of orders from quiz traffic",
    control: ["15% off your first order"],
    variant: ["Your routine, delivered monthly, 15% off every order"],
    result: "Written and queued. Nothing live yet.",
    shipped: "Nothing yet.",
  },
];

export const ADVERTORIAL_PINS = [
  {
    id: "adv-masthead",
    block: "Masthead and disclosure",
    piece: "How obvious the sponsorship is",
    state: "validated",
    hypothesis:
      "A visible disclosure looks less like a trick, so fewer people bounce in the first five seconds and the account stays clear of policy trouble.",
    metric: "Five second bounce rate and ad account rejections",
    control: ["No label"],
    variant: ["Health Desk, plus a plain advertisement label in the masthead"],
    result: "Bounce down 7%, zero rejections in eight weeks against three in the previous six.",
    shipped: "Variant shipped on every advertorial. Non negotiable now.",
  },
  {
    id: "adv-headline",
    block: "Headline and standfirst",
    piece: "Curiosity against the direct claim",
    state: "repeatable",
    hypothesis:
      "A specific mistake in the headline pulls more of the right reader than a general promise, because the wrong reader stops reading sooner.",
    metric: "Read depth past 60% and click through to the quiz",
    control: ["The truth about thyroid supplements"],
    variant: ["Most thyroid supplements are built for a body that isn't yours"],
    result: "Click through to quiz up 18.9%, twice, on separate months.",
    shipped: "Variant shipped and the line was cut down into two ad hooks.",
  },
  {
    id: "adv-story",
    block: "Opening story",
    piece: "First person account against a clinical opening",
    state: "directional",
    hypothesis: "A named person and a date make the first paragraph readable. The clinical opening asks for effort too early.",
    metric: "Scroll depth to the first in article call to action",
    control: ["Hypothyroidism affects roughly one in twenty adults."],
    variant: ["In March, Rachel counted seven bottles on the shelf and could not say what any of them were for."],
    result: "Scroll to the first call to action up 12%. Two weeks in.",
    shipped: "Nothing yet, still splitting.",
  },
  {
    id: "adv-inline",
    block: "First in article call to action",
    piece: "Where the first link appears",
    state: "validated",
    hypothesis: "Placing the first link right after the problem is named catches the reader at their most annoyed, not at the end when they have cooled off.",
    metric: "Click through to the quiz",
    control: ["One call to action at the end of the article"],
    variant: ["Soft link after the problem paragraph, plus the one at the end"],
    result: "Total click through up 26% with no drop in read depth.",
    shipped: "Variant shipped. Three placements maximum, otherwise read depth starts to fall.",
  },
  {
    id: "adv-box",
    block: "Product callout box",
    piece: "Product first against quiz first inside the box",
    state: "fatigued",
    hypothesis: "Showing the bottle in the box should convert the reader who is already sold and does not want a quiz.",
    metric: "Revenue per thousand readers",
    control: ["Take the quiz, then see your match"],
    variant: ["Bottle, price, buy now"],
    result: "Variant won for a month, then refunds rose and the sixty day repeat rate fell behind the quiz path.",
    shipped: "Rolled back to the quiz first box. Revenue per reader is worse on day one and better by day sixty.",
  },
];

export const LISTICLE_PINS = [
  {
    id: "list-headline",
    block: "Headline and count",
    piece: "How many items the headline promises",
    state: "validated",
    hypothesis: "Five reads as finishable. Seven reads as work, and this audience is reading on a phone between other things.",
    metric: "Read depth to the last item",
    control: ["7 things people get wrong about thyroid supplements"],
    variant: ["5 things people with Hashimoto's get wrong about supplements"],
    result: "Read to the last item 34% to 51%. Naming the condition also cut the wrong traffic out.",
    shipped: "Variant shipped. Five items is now the house format.",
  },
  {
    id: "list-item1",
    block: "Item one",
    piece: "Whether the strongest point leads",
    state: "repeatable",
    hypothesis: "The stacking mistake is the one people recognise in themselves, so it earns the first slot over the dosage point.",
    metric: "Scroll past item two",
    control: ["Taking the wrong dose"],
    variant: ["Stacking six bottles that were never designed to work together"],
    result: "Scroll past item two up 14%. Held on a second month.",
    shipped: "Variant shipped and became the lead ad angle for two weeks.",
  },
  {
    id: "list-cta-mid",
    block: "Mid list call to action",
    piece: "Card against an inline text link",
    state: "directional",
    hypothesis: "A card interrupts the read. A text link inside item three should collect the same clicks without breaking the flow.",
    metric: "Click through and read depth together",
    control: ["Full width card after item three"],
    variant: ["Underlined text link inside item three"],
    result: "Clicks down 2%, read depth up 9%, so net sessions to quiz are up. Still running.",
    shipped: "Nothing yet.",
  },
  {
    id: "list-proof",
    block: "Proof block",
    piece: "Review quotes against a lab claim",
    state: "validated",
    hypothesis: "Customer sentences beat a certificate at this point in the read, because the reader is deciding whether people like them use it.",
    metric: "Click through to the quiz",
    control: ["Third party tested, certificate of analysis available"],
    variant: ["Three customer quotes with the condition named"],
    result: "Click through up 15.4% over 19 days.",
    shipped: "Variant shipped. The lab line moved to the FAQ where it answers a real question.",
  },
  {
    id: "list-end",
    block: "End of article offer",
    piece: "Discount first against match first",
    state: "candidate",
    hypothesis: "Readers who reach the end are warm. Leading with the match rather than the discount should protect margin.",
    metric: "Revenue per thousand readers and discount code usage",
    control: ["Get 15% off your first order"],
    variant: ["See which Axos routine fits you, then unlock your offer"],
    result: "Queued, goes live with the next batch.",
    shipped: "Nothing yet.",
  },
];

export const EMAIL_PINS = [
  {
    id: "em-subject",
    block: "Subject line and preheader",
    piece: "Curiosity against the plain instruction",
    state: "validated",
    hypothesis:
      "A specific line about the shelf of bottles gets opened more than a welcome greeting, because it describes the reader rather than the brand.",
    metric: "Open rate, then click to quiz",
    control: ["Welcome to Axos", "Here is 15% off your first order"],
    variant: ["Start here, not with six bottles", "Three questions, then your match."],
    result: "Open rate 41% to 52% across 9,200 sends. Clicks to quiz up 21%.",
    shipped: "Variant shipped as welcome one. The discount line moved to the offer block at the bottom.",
  },
  {
    id: "em-hero",
    block: "Hero image and headline",
    piece: "Product shot against the quiz creative",
    state: "repeatable",
    hypothesis:
      "Reusing the winning ad creative in the first email keeps the promise the ad made, so fewer people feel they landed somewhere else.",
    metric: "Click to quiz from the hero block",
    control: ["Plain bottle on cream"],
    variant: ["The guided way to start creative, the same one that won on Meta"],
    result: "Hero clicks up 17.4%, repeated on a second cohort a month later.",
    shipped: "Variant shipped. The email hero is now swapped whenever the lead ad angle changes.",
  },
  {
    id: "em-pillars",
    block: "Numbered pillars",
    piece: "Three pillars against a long ingredient paragraph",
    state: "validated",
    hypothesis: "Numbered blocks get read on a phone. A paragraph about sourcing does not.",
    metric: "Scroll depth to the product cards",
    control: ["One paragraph on sourcing and testing"],
    variant: ["01 Quiz led, 02 Thyroid focused, 03 Doses on the label"],
    result: "Scroll to the product block up 23%.",
    shipped: "Variant shipped and reused in welcome two.",
  },
  {
    id: "em-products",
    block: "Product cards",
    piece: "Three products against one recommendation",
    state: "fatigued",
    hypothesis: "Showing all three gives the reader a choice, which should lift clicks across the board.",
    metric: "Revenue per send",
    control: ["Hashi-aid only"],
    variant: ["Hashi-aid, Gut support and the duo"],
    result: "Clicks rose, revenue per send fell 8%. Choice was the problem the quiz exists to solve.",
    shipped: "Rolled back in spirit. The cards stayed but every button now points at the quiz, not a product page.",
  },
  {
    id: "em-offer",
    block: "Closing offer block",
    piece: "Subscription framing against a flat discount",
    state: "directional",
    hypothesis: "Framing the 15% as the subscription price rather than a first order coupon should raise subscription share.",
    metric: "Subscription share of first orders",
    control: ["15% off your first order, code START15"],
    variant: ["Your routine, delivered monthly, 15% off every order"],
    result: "Subscription share up 6.4 points, two weeks in. Not called yet.",
    shipped: "Nothing shipped. Variant holds half of sends.",
  },
  {
    id: "em-reminder",
    block: "Welcome two, the reminder",
    piece: "Send delay, 24 hours against 72 hours",
    state: "validated",
    hypothesis: "The reminder works while the ad is still in memory. Three days later it reads as a stranger.",
    metric: "Quiz completions attributed to the reminder",
    control: ["Sent 72 hours after welcome one"],
    variant: ["Sent 24 hours after welcome one, answers preserved in the link"],
    result: "Completions per thousand sends 18 to 31.",
    shipped: "Variant shipped. The flow now waits 24 hours and skips anyone who already completed.",
  },
  {
    id: "em-abandon",
    block: "Quiz abandonment",
    piece: "Naming the exact question they stopped on",
    state: "repeatable",
    hypothesis:
      "Telling them where they stopped, and why that question matters, beats a generic come back line because it answers the reason they hesitated.",
    metric: "Completion rate from the abandonment email",
    control: ["You did not finish your quiz"],
    variant: ["You stopped at question two, the medication one. Nothing there is shared."],
    result: "Completion 12% to 24%. Held across two months.",
    shipped: "Variant shipped for every abandonment point, with the question named dynamically.",
  },
  {
    id: "em-review",
    block: "Post purchase, day 28",
    piece: "Review request against a reorder push",
    state: "candidate",
    hypothesis:
      "Asking about the afternoon, the thing reviews actually mention, should get more reviews than a star request, and the reorder line can ride along underneath.",
    metric: "Reviews per hundred orders and repeat rate at 60 days",
    control: ["Rate your order"],
    variant: ["How are the afternoons? Tell us what changed, then top up at 15% off."],
    result: "Written and queued behind the offer test.",
    shipped: "Nothing yet.",
  },
];
